import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatApiError } from "@/utils/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ["new", "contacted", "completed"];

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    document.title = "Admin — MetalMuse";
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/orders`, { withCredentials: true }).then((r) => setOrders(r.data)).catch(() => {});
    axios.get(`${API}/messages`, { withCredentials: true }).then((r) => setMessages(r.data)).catch(() => {});
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/orders/${id}`, { status }, { withCredentials: true });
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const setPayment = async (id, payment_status) => {
    try {
      await axios.patch(`${API}/orders/${id}`, { payment_status }, { withCredentials: true });
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, payment_status } : o)));
      toast.success(payment_status === "verified" ? "Advance marked as verified" : "Advance marked as pending");
    } catch {
      toast.error("Could not update payment status");
    }
  };

  if (user === undefined) return <div className="pt-40 container-lux text-[#A1A1A6]" data-testid="admin-loading">Loading...</div>;

  if (!user) {
    return (
      <div className="pt-40 pb-32 container-lux max-w-md" data-testid="admin-login-page">
        <Seo title="Admin — MetalMuse" path="/admin" noindex />
        <h1 className="font-tech text-3xl text-[#F5F5F7] mb-2 flex items-center gap-3">
          <img src="/logo.png" alt="MetalMuse logo" className="w-10 h-10 rounded-full ring-1 ring-white/15" />
          Admin Login
        </h1>
        <p className="text-sm text-[#A1A1A6] mb-8">MetalMuse order management</p>
        <form onSubmit={login} data-testid="admin-login-form" className="bg-[#121212] border border-white/5 rounded-2xl p-8 flex flex-col gap-5">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email-input" className="w-full bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#00E5FF]" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="admin-password-input" className="w-full bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#00E5FF]" />
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-button" className="bg-white text-black font-tech text-xs uppercase tracking-[0.2em] font-semibold px-6 py-3 rounded-full hover:bg-cyan-200 transition-colors duration-300">
            Log In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-32 container-lux" data-testid="admin-dashboard">
      <Seo title="Admin — MetalMuse" path="/admin" noindex />
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-tech text-3xl text-[#F5F5F7]">Order Dashboard</h1>
          <p className="text-sm text-[#A1A1A6] mt-1">Signed in as {user.email}</p>
        </div>
        <button onClick={logout} data-testid="admin-logout-button" className="text-sm border border-white/20 rounded-full px-5 py-2.5 hover:bg-white/5 transition-colors duration-300">
          Log out
        </button>
      </div>

      <div className="flex gap-3 mb-10">
        {["orders", "messages"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`px-5 py-2.5 rounded-full text-sm font-medium capitalize transition-colors duration-300 border ${tab === t ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "text-[#A1A1A6] border-white/15 hover:text-white"}`}>
            {t} ({t === "orders" ? orders.length : messages.length})
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="flex flex-col gap-5" data-testid="admin-orders-list">
          {orders.length === 0 && <p className="text-[#A1A1A6]" data-testid="admin-orders-empty">No order requests yet.</p>}
          {orders.map((o) => (
            <Reveal key={o.id}>
              <div data-testid={`admin-order-${o.id.slice(0, 8)}`} className="bg-[#121212] border border-white/5 rounded-2xl p-6 flex flex-col md:flex-row gap-6">
                {o.photo_url && (
                  <a href={`${BACKEND}${o.photo_url}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-order-photo-${o.id.slice(0, 8)}`}>
                    <img src={`${BACKEND}${o.photo_url}`} alt="Customer upload" className="w-28 h-28 object-cover rounded-xl border border-white/10" />
                  </a>
                )}
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-semibold text-[#F5F5F7]">{o.name}</p>
                    <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full ${o.status === "new" ? "bg-[#00E5FF]/15 text-[#00E5FF]" : o.status === "contacted" ? "bg-blue-500/15 text-blue-400" : "bg-green-500/15 text-green-400"}`}>
                      {o.status}
                    </span>
                  </div>
                  <p className="text-sm text-[#A1A1A6] mt-2">{o.product_size} × {o.quantity} · {o.delivery_method}{o.same_day ? " (same-day +$5)" : ""}</p>
                  <p className="text-sm text-[#A1A1A6] mt-1">{o.email} · {o.phone}</p>
                  {o.payment_total > 0 && (
                    <p className="text-sm mt-2 text-[#A1A1A6] flex flex-wrap items-center gap-x-2 gap-y-1">
                      Interac e-Transfer — total <span className="text-[#F5F5F7] font-medium">${Number(o.payment_total).toFixed(2)}</span> · 50% advance <span className="text-[#00E5FF] font-medium">${Number(o.payment_advance).toFixed(2)}</span>
                      <span data-testid={`admin-payment-badge-${o.id.slice(0, 8)}`} className={`text-[10px] uppercase tracking-widest px-2.5 py-0.5 rounded-full ${o.payment_status === "verified" ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"}`}>
                        {o.payment_status === "verified" ? "Advance verified" : "Payment pending"}
                      </span>
                      <button onClick={() => setPayment(o.id, o.payment_status === "verified" ? "pending" : "verified")} data-testid={`admin-payment-toggle-${o.id.slice(0, 8)}`} className="text-xs text-[#00E5FF] underline underline-offset-4 hover:text-[#5CF0FF] transition-colors duration-300">
                        {o.payment_status === "verified" ? "Mark pending" : "Mark verified"}
                      </button>
                      {o.payment_screenshot_url && (
                        <> · <a href={`${BACKEND}${o.payment_screenshot_url}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-payment-shot-${o.id.slice(0, 8)}`} className="text-[#00E5FF] underline underline-offset-4">View payment screenshot</a></>
                      )}
                    </p>
                  )}
                  {o.notes && <p className="text-sm text-[#A1A1A6]/80 mt-2 italic">"{o.notes}"</p>}
                  <p className="text-xs text-[#A1A1A6]/60 mt-2">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <div className="flex md:flex-col gap-2 shrink-0">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(o.id, s)} data-testid={`admin-order-status-${s}-${o.id.slice(0, 8)}`} className={`text-xs capitalize px-4 py-2 rounded-full border transition-colors duration-300 ${o.status === s ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "border-white/15 text-[#A1A1A6] hover:text-white"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}

      {tab === "messages" && (
        <div className="flex flex-col gap-5" data-testid="admin-messages-list">
          {messages.length === 0 && <p className="text-[#A1A1A6]" data-testid="admin-messages-empty">No messages yet.</p>}
          {messages.map((m) => (
            <Reveal key={m.id}>
              <div data-testid={`admin-message-${m.id.slice(0, 8)}`} className="bg-[#121212] border border-white/5 rounded-2xl p-6">
                <p className="font-semibold text-[#F5F5F7]">{m.name}</p>
                <p className="text-sm text-[#A1A1A6] mt-1">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
                <p className="text-sm text-[#F5F5F7]/80 mt-3 leading-relaxed">{m.message}</p>
                <p className="text-xs text-[#A1A1A6]/60 mt-3">{new Date(m.created_at).toLocaleString()}</p>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
